import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Input, Modal, Space, Spin, Switch, message, Radio, RadioChangeEvent } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import CommonTable from 'components/CommonTable';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import {
    EOriderType,
    EProviderListMappingStatusValue,
    EProviderMappingStatusValue,
    IBPMappingItem,
    IGetProviderMappingDataInput,
    IProviderMappingDataResp,
    EGrowthTier,
} from 'types/operation';
import { getProviderNameDisplay } from 'utils/provider';
import type { CheckboxOptionType, CheckboxValueType } from 'antd/es/checkbox/Group';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import dayjs from 'dayjs';
import OpsProviderMappingListStatusTag from 'components/OpsProviderMappingListStatusTag';
import { getHelloKlairtyProfileLink, getPrivateFilePreview, saveHelloKlarityProviderMappingData } from 'api/operation';
import useGetSettingData from 'hooks/useGetSettingData';
import { EnumFields } from 'types/enumerationData';
import AccessStore from 'store/Access/permission';
import { PermissionModuleKeys } from 'constants/access';
import { prereleaseOriginReplacement } from 'utils/helloklarity';
import { PRE_RELEASE_ORIGIN } from 'constants/common';
import MulitProfileLinkModal from '../../MulitProfileLinkModal';
import useGetProviderMappingData from 'hooks/helloklarityOps/useGetProviderMappingData';
import { BudgetStatus } from 'types/marketing';
import { confirmRelistIfOverdue } from 'utils/relistOverdue';
import s from '../s.module.less';

interface IProps {
    showMappingModal: () => void;
    hideMappingConfirmModal: () => void;
    showMappingConfirmModal: () => void;
    showSlugModal: () => void;
    showTitleModal: () => void;
    showPatientRatingModal: () => void;
    showAutoScheduleModal: () => void;
    showSFUModal: () => void;
    handleSetCurrentRow: (id: IBPMappingItem | null) => void;
    forceReload: Record<any, any>;
    handleListingStatusChange: (id: number, status: EProviderListMappingStatusValue) => void;
    findProviderByEmail: (email: string, type: number) => Promise<any>;
}

const ProvidersTable = ({
    showMappingModal,
    showSlugModal,
    hideMappingConfirmModal,
    showMappingConfirmModal,
    showPatientRatingModal,
    handleSetCurrentRow,
    handleListingStatusChange,
    forceReload,
    findProviderByEmail,
}: IProps) => {
    const navigate = useNavigate();
    const [getAccessStore] = AccessStore.useStore();
    const access = getAccessStore('data');
    const [providerStatus] = useGetSettingData(EnumFields.PROVIDER_MAPPING_LIST_STATUS, true) as CheckboxOptionType[][];
    const providerListingStatus = providerStatus?.filter((option) => option.value !== 'PR');
    const [providerMappingStatusSettingData] = useGetSettingData(
        EnumFields.PROVIDER_MAPPING_STATUS,
        true,
    ) as CheckboxOptionType[][];
    const providerMappingStatus = providerMappingStatusSettingData?.filter(
        (option) => option.value !== EProviderMappingStatusValue.AIR_TABLE_MAPPING,
    );
    const [patientRatingStatus] = useGetSettingData(EnumFields.PROVIDER_PATIENT_RATING, true) as CheckboxOptionType[][];
    const [pageLoading, setPageLoading] = useState(false);
    const [relistLoadingId, setRelistLoadingId] = useState<number | undefined>(undefined);
    const [orderField, setOrderKey] = useState<string>('');

    const providerMicStatus = useMemo(
        () => [
            { label: 'Effective', value: 'Effective' },
            { label: 'None', value: 'None' },
            { label: 'Expired', value: 'Expired' },
        ],
        [],
    );

    const providerGrowthTier = useMemo(
        () => [
            { label: 'Starter', value: EGrowthTier.Starter },
            { label: 'Growth', value: EGrowthTier.Growth },
            { label: 'Pro-listings', value: EGrowthTier.Scaling },
        ],
        [],
    );

    const editPermission = true;
    const ratingPermission = useMemo(
        () => !!(access && access[PermissionModuleKeys.KLARITY_PROVIDER_KIWI_RATING]),
        [access],
    );

    const [pendingNameFilter, setPendingNameFilter] = useState('');
    const [nameFilter, setNameFilter] = useState('');
    const [pendingSlugFilter, setPendingSlugFilter] = useState('');
    const [slugFilter, setSlugFilter] = useState('');
    const [pendingUidFilter, setPendingUidFilter] = useState('');
    const [uidFilter, setUidFilter] = useState('');
    const [current, setCurrent] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [orderType, setOrderType] = useState<EOriderType | undefined>(EOriderType.DESC);

    const [listingStatusIndeterminate, setListingStatusIndeterminate] = useState(false);
    const [listingStatusCheckAll, setListingStatusCheckAll] = useState(false);
    const [listingStatusList, setListingStatusList] = useState<Array<CheckboxValueType>>([]);

    const [mappingStatusIndeterminate, setMappingStatusIndeterminate] = useState(false);
    const [mappingStatusCheckAll, setMappingStatusCheckAll] = useState(false);
    const [mappingStatusList, setMappingStatusList] = useState<Array<CheckboxValueType>>([]);

    const [bookingLinkList, setBookingLinkList] = useState<Array<CheckboxValueType>>([]);

    const [patientRatingIndeterminate, setPatientRatingIndeterminate] = useState(false);
    const [patientRatingCheckAll, setPatientRatingCheckAll] = useState(false);
    const [patientRatingList, setPatientRatingList] = useState<Array<CheckboxValueType>>([]);

    const [micStatusIndeterminate, setMicStatusIndeterminate] = useState(false);
    const [micStatusCheckAll, setMicStatusCheckAll] = useState(false);
    const [micStatusList, setMicStatusList] = useState<Array<CheckboxValueType>>([]);

    const [growthTierIndeterminate, setGrowthTierIndeterminate] = useState(false);
    const [growthTierCheckAll, setGrowthTierCheckAll] = useState(false);
    const [growthTierList, setGrowthTierList] = useState<Array<CheckboxValueType>>([]);

    const [gpFilterIndeterminate, setGpFilterIndeterminate] = useState(false);
    const [gpFilterCheckAll, setGpFilterCheckAll] = useState(false);
    const [gpFilterList, setGpFilterList] = useState<Array<CheckboxValueType>>([]);

    const [chargePerAppt, setChargePerAppt] = useState<boolean | string>('all');

    const [showProfileLinkModal, setShowProfileLinkModal] = useState(false);
    const [currentProfileLinks, setCurrentProfileLinks] = useState([]);

    const [ehrStatusFilter, setEhrStatusFilter] = useState<Array<CheckboxValueType>>([]);
    const [programIdListFilter, setProgramIdListFilter] = useState<Array<number>>([]);

    const [data = {}, tableLoading, tableError, bpRefetch] = useGetProviderMappingData();

    const { records, total = 0 } = data as IProviderMappingDataResp;

    useEffect(() => {
        if (tableError) {
            message.error(tableError);
        }
    }, [tableError]);

    const handleTableChange = useCallback(
        (paginationData?: TablePaginationConfig, filtersData?: any, sorter?: any, action?: any) => {
            setCurrent(paginationData?.current!);
            setPageSize(paginationData?.pageSize!);

            if (action?.action === 'sort') {
                const orderKey = sorter.order === 'ascend' ? EOriderType.ASC : EOriderType.DESC;
                setOrderKey(sorter.field === 'firstLaunchAt' ? 'first_launch_at' : sorter.field);
                setOrderType(orderKey);
            }

            setProgramIdListFilter(filtersData.programList);
        },
        [],
    );

    const handleRefetch = useCallback(() => {
        const params: IGetProviderMappingDataInput = {
            bookLink: bookingLinkList as number[],
            current,
            intakeqProviderId: '',
            listingStatus: listingStatusList as string[],
            mappingStatus: mappingStatusList as string[],
            micStatus: micStatusList as string[],
            growthTier: growthTierList as EGrowthTier[],
            name: nameFilter,
            orderKey: orderField,
            orderType,
            patientRating: patientRatingList as number[],
            providerNameSlug: slugFilter,
            size: pageSize,
            title: '',
            type: 3,
            uidPublic: uidFilter,
            programIdList: programIdListFilter,
            leadGenFeePerAppointment: chargePerAppt === 'all' ? undefined : (chargePerAppt as boolean),
            hasGroupPractice: true,
            gpFilter: gpFilterList as string[],
        };
        if (ehrStatusFilter.length === 1) {
            params.ehrSetUpStatus = [ehrStatusFilter[0] as boolean];
        }

        bpRefetch(params);
    }, [
        forceReload,
        bookingLinkList,
        bpRefetch,
        current,
        handleTableChange,
        listingStatusList,
        mappingStatusList,
        nameFilter,
        orderType,
        pageSize,
        patientRatingList,
        slugFilter,
        micStatusList,
        ehrStatusFilter,
        uidFilter,
        orderField,
        programIdListFilter,
        chargePerAppt,
        growthTierList,
        gpFilterList,
    ]);

    useEffect(() => {
        handleRefetch();
    }, [handleRefetch]);

    const handleListingStatusFilterCheckAll = useCallback(
        (e: CheckboxChangeEvent) => {
            const checkedListData =
                e.target.checked && providerListingStatus
                    ? providerListingStatus.map((option: CheckboxOptionType) => option.value)
                    : [];
            setListingStatusList(checkedListData);
            setListingStatusIndeterminate(false);
            setListingStatusCheckAll(e.target.checked);
            setCurrent(1);
        },
        [providerListingStatus],
    );

    const handleListingStatusFilter = useCallback(
        (statusList: CheckboxValueType[]) => {
            setListingStatusList(statusList);
            setListingStatusIndeterminate(
                !!statusList.length && !!providerListingStatus && statusList.length < providerListingStatus.length,
            );
            setListingStatusCheckAll(statusList.length === providerListingStatus?.length);
            setCurrent(1);
        },
        [providerListingStatus],
    );

    const handleMappingStatusFilterCheckAll = useCallback(
        (e: CheckboxChangeEvent) => {
            const checkedListData =
                e.target.checked && providerMappingStatus
                    ? providerMappingStatus.map((option: CheckboxOptionType) => option.value)
                    : [];
            setMappingStatusList(checkedListData);
            setMappingStatusIndeterminate(false);
            setMappingStatusCheckAll(e.target.checked);
            setCurrent(1);
        },
        [providerMappingStatus],
    );

    const handleMappingStatusFilter = useCallback(
        (statusList: CheckboxValueType[]) => {
            setMappingStatusList(statusList);
            setMappingStatusIndeterminate(
                !!statusList.length && !!providerMappingStatus && statusList.length < providerMappingStatus.length,
            );
            setMappingStatusCheckAll(statusList.length === providerMappingStatus?.length);
            setCurrent(1);
        },
        [providerMappingStatus],
    );

    const handlePatientRatingFilterCheckAll = useCallback(
        (e: CheckboxChangeEvent) => {
            const checkedListData =
                e.target.checked && patientRatingStatus
                    ? patientRatingStatus.map((option: CheckboxOptionType) => option.value)
                    : [];
            setPatientRatingList(checkedListData);
            setPatientRatingIndeterminate(false);
            setPatientRatingCheckAll(e.target.checked);
            setCurrent(1);
        },
        [patientRatingStatus],
    );

    const handlePatientRatingFilter = useCallback(
        (statusList: CheckboxValueType[]) => {
            setPatientRatingList(statusList);
            setPatientRatingIndeterminate(
                !!statusList.length && !!patientRatingStatus && statusList.length < patientRatingStatus.length,
            );
            setPatientRatingCheckAll(statusList.length === patientRatingStatus?.length);
            setCurrent(1);
        },
        [patientRatingStatus],
    );

    const handleMicStatusFilterCheckAll = useCallback(
        (e: CheckboxChangeEvent) => {
            const checkedListData =
                e.target.checked && providerMicStatus
                    ? providerMicStatus.map((option: CheckboxOptionType) => option.value)
                    : [];
            setMicStatusList(checkedListData);
            setMicStatusIndeterminate(false);
            setMicStatusCheckAll(e.target.checked);
            setCurrent(1);
        },
        [providerMicStatus],
    );

    const handleMicStatusFilter = useCallback(
        (statusList: CheckboxValueType[]) => {
            setMicStatusList(statusList);
            setMicStatusIndeterminate(
                !!statusList.length && !!providerMicStatus && statusList.length < providerMicStatus.length,
            );
            setMicStatusCheckAll(statusList.length === providerMicStatus?.length);
            setCurrent(1);
        },
        [providerMicStatus],
    );

    const handleGrowthTierFilterCheckAll = useCallback(
        (e: CheckboxChangeEvent) => {
            const checkedListData =
                e.target.checked && providerGrowthTier
                    ? providerGrowthTier.map((option: CheckboxOptionType) => option.value)
                    : [];
            setGrowthTierList(checkedListData);
            setGrowthTierIndeterminate(false);
            setGrowthTierCheckAll(e.target.checked);
            setCurrent(1);
        },
        [providerGrowthTier],
    );

    const handleGrowthTierFilter = useCallback(
        (statusList: CheckboxValueType[]) => {
            setGrowthTierList(statusList);
            setGrowthTierIndeterminate(
                !!statusList.length && !!providerGrowthTier && statusList.length < providerGrowthTier.length,
            );
            setGrowthTierCheckAll(statusList.length === providerGrowthTier?.length);
            setCurrent(1);
        },
        [providerGrowthTier],
    );

    const gpFilterOptions = useMemo(
        () => [
            { label: 'Solo only', value: 'SOLO' },
            { label: 'Group practice owner only', value: 'OWNER' },
            { label: 'Group practice provider only', value: 'PROVIDER' },
            { label: 'Pending group practice review', value: 'PENDING' },
        ],
        [],
    );

    const handleGpFilterCheckAll = useCallback(
        (e: CheckboxChangeEvent) => {
            const checkedListData = e.target.checked ? gpFilterOptions.map((option) => option.value) : [];
            setGpFilterList(checkedListData);
            setGpFilterIndeterminate(false);
            setGpFilterCheckAll(e.target.checked);
            setCurrent(1);
        },
        [gpFilterOptions],
    );

    const handleGpFilter = useCallback(
        (statusList: CheckboxValueType[]) => {
            setGpFilterList(statusList);
            setGpFilterIndeterminate(!!statusList.length && statusList.length < gpFilterOptions.length);
            setGpFilterCheckAll(statusList.length === gpFilterOptions.length);
            setCurrent(1);
        },
        [gpFilterOptions],
    );

    const handleAutoChargePerApptFilter = useCallback((e: RadioChangeEvent) => {
        setChargePerAppt(e.target.value);
        setCurrent(1);
    }, []);

    const handleShowMappingModal = useCallback(
        (row: IBPMappingItem) => {
            if (editPermission) {
                handleSetCurrentRow(row);
                showMappingModal();
            }
        },
        [handleSetCurrentRow, showMappingModal, editPermission],
    );

    const handleShowSlugModal = useCallback(
        (row: IBPMappingItem) => {
            if (editPermission) {
                handleSetCurrentRow(row);
                showSlugModal();
            }
        },
        [showSlugModal, handleSetCurrentRow, editPermission],
    );

    const handleShowPatientRatingModal = (row: IBPMappingItem) => {
        if (ratingPermission) {
            handleSetCurrentRow(row);
            showPatientRatingModal();
        }
    };

    const handleGenerateProfileLink = useCallback(async (id: number) => {
        if (id) {
            setPageLoading(true);
            try {
                const res = await getHelloKlairtyProfileLink(id);
                if (res && !res.error) {
                    if (res.data?.data && res.data?.data.length > 1) {
                        const profileLinks = res.data?.data.map((link: string) =>
                            prereleaseOriginReplacement(link, PRE_RELEASE_ORIGIN),
                        );
                        setCurrentProfileLinks(profileLinks);
                        setShowProfileLinkModal(true);
                    } else if (res.data?.data?.[0]) {
                        const newUrl = prereleaseOriginReplacement(res.data?.data[0], PRE_RELEASE_ORIGIN);
                        window.open(newUrl, '_blank');
                    } else {
                        message.error('This provider has no profile link yet.');
                    }
                } else {
                    message.error(res.error);
                }
            } catch (e) {
                console.error(e);
            }
            setPageLoading(false);
        }
    }, []);

    const handleReList = useCallback(
        async (item: IBPMappingItem) => {
            if (!editPermission) return;
            const doRelist = async () => {
                handleSetCurrentRow(item);
                try {
                    const res = await findProviderByEmail(item.email, 2);
                    if (res && !res.error) {
                        const providerRecords = res.data?.data?.records;
                        if (providerRecords && providerRecords.length > 0) {
                            const { mappingStatus } = providerRecords[0];
                            if (mappingStatus === EProviderMappingStatusValue.AIR_TABLE_MAPPING) {
                                showMappingConfirmModal();
                                return;
                            }
                        }
                    } else {
                        message.error(res?.error);
                    }
                    handleListingStatusChange(item.id, EProviderListMappingStatusValue.LISTED);
                    hideMappingConfirmModal();
                } catch (e) {
                    console.error(e);
                }
            };
            setRelistLoadingId(item.id);
            try {
                await confirmRelistIfOverdue(item.bpProviderId, doRelist);
            } finally {
                setRelistLoadingId(undefined);
            }
        },
        [editPermission, findProviderByEmail, handleListingStatusChange],
    );

    const tryView = async (resourceId: string) => {
        const result = await getPrivateFilePreview(resourceId);
        if (!result.error) {
            const url = result?.data?.data?.singedUrl;
            if (url) {
                window.open(url);
            } else {
                message.error('Failed to get the file preview');
            }
        }
    };

    const handleChargePerApptChange = useCallback(async (checked: boolean, id: number) => {
        try {
            const res = await saveHelloKlarityProviderMappingData({
                bpProviderId: id,
                leadGenFeePerAppointment: checked,
            });
            if (res && !res.error) {
                message.success('Updated successfully');
                handleRefetch();
            } else {
                message.error(res?.error);
            }
        } catch (e) {
            console.error(e);
        }
    }, []);

    const columns: ColumnsType<any> = [
        {
            title: 'NAME',
            dataIndex: 'name',
            filterSearch: true,
            className: s.tableColumn,
            filterIcon: <SearchOutlined />,
            width: '280px',
            fixed: 'left',
            filterDropdown: ({ close }) => (
                <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        placeholder="Search name"
                        value={pendingNameFilter}
                        onChange={(e) => setPendingNameFilter(e.target.value)}
                        onPressEnter={() => {
                            setNameFilter(pendingNameFilter);
                            setCurrent(1);
                            close();
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setNameFilter(pendingNameFilter);
                                setCurrent(1);
                                close();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingNameFilter('');
                                setNameFilter('');
                                setCurrent(1);
                                close();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            render: (name: string, row: IBPMappingItem) => {
                const { firstName, lastName, email, conflict } = row;
                return (
                    <div className={s.nameWrap}>
                        {conflict && <div className={s.warning} onClick={() => handleShowMappingModal(row)} />}
                        <div className={s.name}>
                            {getProviderNameDisplay({ firstName, lastName })}
                            <div className={s.email}>{email}</div>
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'UID',
            dataIndex: 'uidPublic',
            filterSearch: true,
            className: s.tableColumn,
            filterIcon: <SearchOutlined />,
            width: '100px',
            fixed: 'left',
            filterDropdown: ({ close }) => (
                <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        placeholder="Search UID"
                        value={pendingUidFilter}
                        onChange={(e) => setPendingUidFilter(e.target.value)}
                        onPressEnter={() => {
                            setUidFilter(pendingUidFilter);
                            setCurrent(1);
                            close();
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setUidFilter(pendingUidFilter);
                                setCurrent(1);
                                close();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingUidFilter('');
                                setUidFilter('');
                                setCurrent(1);
                                close();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            render: (uid: string) => <div className={s.nameWrap}>{uid}</div>,
        },
        {
            title: 'PROFILE STATUS',
            dataIndex: 'listingStatus',
            key: 'listingStatus',
            width: '150px',
            className: s.tableColumn,
            filterDropdown: () => (
                <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Space>
                        <Checkbox
                            indeterminate={listingStatusIndeterminate}
                            onChange={handleListingStatusFilterCheckAll}
                            checked={listingStatusCheckAll}
                        >
                            Check all
                        </Checkbox>
                        <Checkbox.Group
                            options={providerListingStatus}
                            value={listingStatusList}
                            onChange={handleListingStatusFilter}
                        />
                    </Space>
                </div>
            ),
            render: (status) => status && <OpsProviderMappingListStatusTag status={status} />,
        },
        {
            title: 'GROUP PRACTICE',
            dataIndex: 'groupPractice',
            key: 'groupPractice',
            width: '200px',
            className: s.tableColumn,
            filterDropdown: () => (
                <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <Checkbox
                            style={{ marginLeft: 0 }}
                            indeterminate={gpFilterIndeterminate}
                            onChange={handleGpFilterCheckAll}
                            checked={gpFilterCheckAll}
                        >
                            Check all
                        </Checkbox>
                        {gpFilterOptions.map((option) => (
                            <Checkbox
                                key={option.value}
                                style={{ marginLeft: 0 }}
                                checked={(gpFilterList as string[]).includes(option.value)}
                                onChange={(e) => {
                                    const next = e.target.checked
                                        ? [...gpFilterList, option.value]
                                        : gpFilterList.filter((v) => v !== option.value);
                                    handleGpFilter(next);
                                }}
                            >
                                {option.label}
                            </Checkbox>
                        ))}
                    </div>
                </div>
            ),
            render: (_: any, row: IBPMappingItem) => {
                const gp = row.groupPractice;
                if (!gp) {
                    return <span style={{ color: '#999' }}>—</span>;
                }
                return (
                    <div>
                        <a
                            className={s.gpLink}
                            onClick={() => {
                                if (gp.organizationId) {
                                    window.open(`/operation/group-practice-detail/${gp.organizationId}`, '_blank');
                                }
                            }}
                        >
                            {gp.name}
                        </a>
                        <div style={{ fontSize: 12, color: '#999' }}>{gp.role}</div>
                    </div>
                );
            },
        },
        {
            title: 'AUTO CHARGE PER APPT',
            dataIndex: 'leadGenFeePerAppointment',
            key: 'leadGenFeePerAppointment',
            width: '220px',
            className: s.tableColumn,
            render: (status, row: IBPMappingItem) => (
                <Switch checked={status} onChange={(checked) => handleChargePerApptChange(checked, row.bpProviderId)} />
            ),
            filterDropdown: () => (
                <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Space>
                        <Radio.Group
                            options={[
                                { label: 'Check all', value: 'all' },
                                { label: 'Yes', value: true },
                                { label: 'No', value: false },
                            ]}
                            value={chargePerAppt}
                            onChange={handleAutoChargePerApptFilter}
                        />
                    </Space>
                </div>
            ),
        },
        {
            title: 'MALPRACTICE INSURANCE',
            dataIndex: 'micStatus',
            key: 'micStatus',
            width: '220px',
            className: s.tableColumn,
            filterDropdown: () => (
                <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Space>
                        <Checkbox
                            indeterminate={micStatusIndeterminate}
                            onChange={handleMicStatusFilterCheckAll}
                            checked={micStatusCheckAll}
                        >
                            Check all
                        </Checkbox>
                        <Checkbox.Group
                            options={providerMicStatus}
                            value={micStatusList}
                            onChange={handleMicStatusFilter}
                        />
                    </Space>
                </div>
            ),
            render: (status) => status || '-',
        },
        {
            title: 'PRO-LISTING',
            dataIndex: 'budgetStatus',
            key: 'budgetStatus',
            width: '150px',
            className: s.tableColumn,
            render: (status) => (status === BudgetStatus.ACTIVE ? 'Active' : 'Inactive'),
        },
        {
            title: 'GROWTH TARGET',
            dataIndex: 'growthTier',
            key: 'growthTier',
            width: '150px',
            className: s.tableColumn,
            filterDropdown: () => (
                <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Space>
                        <Checkbox
                            indeterminate={growthTierIndeterminate}
                            onChange={handleGrowthTierFilterCheckAll}
                            checked={growthTierCheckAll}
                        >
                            Check all
                        </Checkbox>
                        <Checkbox.Group
                            options={providerGrowthTier}
                            value={growthTierList}
                            onChange={handleGrowthTierFilter}
                        />
                    </Space>
                </div>
            ),
            render: (status) => providerGrowthTier.find((item) => item.value === status)?.label || 'None',
        },
        {
            title: 'W-9 FORM',
            dataIndex: 'w9Form',
            key: 'w9Form',
            width: '220px',
            className: s.tableColumn,
            render: (w9: string) => {
                if (!w9) return <span>-</span>;
                return (
                    <span className={s.link} onClick={() => tryView(w9)}>
                        View
                    </span>
                );
            },
        },
        {
            title: 'PROFILE LINK',
            dataIndex: 'profileLink',
            key: 'bookingLink',
            width: '170px',
            className: s.tableColumn,
            render: (v, item: IBPMappingItem) => {
                const { listingStatus } = item;
                if (
                    listingStatus !== EProviderListMappingStatusValue.PENDING_REVIEW &&
                    listingStatus !== EProviderListMappingStatusValue.DISQUALIFIED
                ) {
                    return (
                        <a className={s.profileLink} onClick={() => handleGenerateProfileLink(item.id)}>
                            Link
                        </a>
                    );
                }
                return null;
            },
        },
        {
            title: 'PATIENT RATING',
            dataIndex: 'patientRating',
            key: 'patientRating',
            width: '170px',
            className: s.tableColumn,
            filterDropdown: () => (
                <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Space>
                        <Checkbox
                            indeterminate={patientRatingIndeterminate}
                            onChange={handlePatientRatingFilterCheckAll}
                            checked={patientRatingCheckAll}
                        >
                            Check all
                        </Checkbox>
                        <Checkbox.Group
                            options={patientRatingStatus}
                            value={patientRatingList}
                            onChange={handlePatientRatingFilter}
                        />
                    </Space>
                </div>
            ),
            render: (val: string, row: IBPMappingItem) => {
                const label = patientRatingStatus?.find((e) => val.toString() === e.value.toString())?.label || '';
                return (
                    <div className={s.slug}>
                        {label}
                        {ratingPermission && (
                            <div className={s.editIcon} onClick={() => handleShowPatientRatingModal(row)} />
                        )}
                    </div>
                );
            },
        },
        {
            title: 'SLUG',
            dataIndex: 'providerNameSlug',
            width: '180px',
            className: s.tableColumn,
            filterSearch: true,
            filterIcon: <SearchOutlined />,
            filterDropdown: ({ close }) => (
                <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        placeholder="Search slug"
                        value={pendingSlugFilter}
                        onChange={(e) => setPendingSlugFilter(e.target.value)}
                        onPressEnter={() => {
                            setSlugFilter(pendingSlugFilter);
                            setCurrent(1);
                            close();
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setSlugFilter(pendingSlugFilter);
                                setCurrent(1);
                                close();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingSlugFilter('');
                                setSlugFilter('');
                                setCurrent(1);
                                close();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            render: (text, row) => (
                <div className={s.slug}>
                    {text}
                    {editPermission && <div className={s.editIcon} onClick={() => handleShowSlugModal(row)} />}
                </div>
            ),
        },
        {
            title: 'LAST MODIFIED',
            dataIndex: 'lastUpdateAt',
            sortDirections: ['ascend', 'descend', 'ascend'],
            sorter: true,
            width: '150px',
            className: s.tableColumn,
            render: (time) => <div>{time ? dayjs(time).format('MM/DD/YYYY') : 'Nil'}</div>,
        },
        {
            title: 'KLARITY LAUNCH DATE',
            dataIndex: 'firstLaunchAt',
            width: '250px',
            className: s.tableColumn,
            sorter: true,
            sortDirections: ['ascend', 'descend', 'ascend'],
            render: (date?: string) => (date ? dayjs(date).format('MM/DD/YYYY') : 'Not launched yet'),
        },
        editPermission
            ? {
                  title: '',
                  width: '200px',
                  className: s.tableColumn,
                  fixed: 'right',
                  render: (item: IBPMappingItem) => {
                      if (item.listingStatus === EProviderListMappingStatusValue.UNLISTED) {
                          return (
                              <Button
                                  className={s.link}
                                  onClick={() => handleReList(item)}
                                  type="text"
                                  loading={relistLoadingId === item.id}
                              >
                                  Re-list
                              </Button>
                          );
                      }
                      if (item.listingStatus === EProviderListMappingStatusValue.PAUSE_LISTING && item.hasBookingLink) {
                          return (
                              <Button
                                  className={s.link}
                                  onClick={() => handleReList(item)}
                                  type="text"
                                  loading={relistLoadingId === item.id}
                              >
                                  Re-list
                              </Button>
                          );
                      }
                      if (item.listingStatus === EProviderListMappingStatusValue.LISTED) {
                          return (
                              <Button
                                  className={s.link}
                                  onClick={() => {
                                      if (editPermission) {
                                          handleListingStatusChange(item.id, EProviderListMappingStatusValue.UNLISTED);
                                      }
                                  }}
                                  style={{ color: '#C45407' }}
                                  type="text"
                              >
                                  Delete Listing (Danger)
                              </Button>
                          );
                      }
                  },
              }
            : {},
        {
            title: '',
            width: '100px',
            className: s.tableColumn,
            fixed: 'right',
            render: (item: IBPMappingItem) => {
                if (item.listingStatus !== EProviderListMappingStatusValue.UNIPROFILE_NEEDED) {
                    return (
                        <Button
                            className={s.link}
                            onClick={() => {
                                const { conflict, listingStatus, mappingStatus } = item;
                                const url = `/operation/${PATH.PPA_PROVIDER_DETAIL}/-1/${item.bpProviderId}?id=${
                                    item.id
                                }&providerId=${item.bpProviderId}&slug=${encodeURIComponent(
                                    item.providerNameSlug,
                                )}&conflict=${!!conflict}&profileStatus=${listingStatus}&mappingStatus=${mappingStatus}`;
                                window.open(url, '_blank');
                            }}
                            type="text"
                        >
                            View
                        </Button>
                    );
                }
            },
        },
    ];

    return (
        <Spin spinning={pageLoading}>
            <MulitProfileLinkModal
                show={showProfileLinkModal}
                onCancel={() => setShowProfileLinkModal(false)}
                onOk={() => setShowProfileLinkModal(false)}
                profileLinks={currentProfileLinks}
            />
            <CommonTable<any>
                rowKey={(record: any) => record.id}
                bordered
                columns={columns}
                data={records || []}
                onChange={handleTableChange}
                pagination={{
                    current,
                    pageSize,
                    total,
                }}
                loading={tableLoading}
                scroll={{ x: '1200' }}
            />
        </Spin>
    );
};

export default ProvidersTable;
